import * as yup from "yup"
import { DateTime } from "luxon"
import { Form } from "formik"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../../currency/currencyLib"
import { CreateUpdateDayMenuFormValues, SignageType } from "../../menuPlanningTypes"
import { CreateUpdateDayMenuFormContent } from "./CreateUpdateDayMenuFormContent"
import { defaultDish } from "./DishList"
import { useDateTime } from "../../../../../localization/useDateTime"

type CreateUpdateDayMenuFormProps = Readonly<{
    initialValues?: CreateUpdateDayMenuFormValues | null
    onSubmit: (formData: any) => void
    dateInfo?: DateTime
    signageTypes?: SignageType[]
    onSaveSignageTypes?: (selectedSignageTypes: string[]) => void
}>

export const CreateUpdateDayMenuForm = ({
    initialValues,
    onSubmit,
    dateInfo,
    onSaveSignageTypes,
    signageTypes,
}: CreateUpdateDayMenuFormProps) => {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()

    const defaultInitialValues = {
        signageTypes: signageTypes ?? [SignageType.WEB_MENU],
        publishDate: dateInfo || today,
        dishes: [defaultDish],
        isActive: true,
        color: null,
    }

    const validationSchema = yup.object({
        signageTypes: yup
            .array()
            .min(1, l10n.getString("merchant-menu-planning-dialog-signageTypes-min-error"))
            .required(l10n.getString("required-field")),
        color: yup
            .object()
            .shape({
                name: yup.string(),
                code: yup.string(),
            })
            .required(l10n.getString("required-field")),
    })

    function handleSubmit(values: CreateUpdateDayMenuFormValues) {
        const { dishes, recurrence, publishDate, color, ...dayMenuValues } = values

        if (onSaveSignageTypes) {
            onSaveSignageTypes(values.signageTypes)
        }

        const modifyDayMenu = {
            ...dayMenuValues,
            publishDate: publishDate.toISODate(),
            color: color?.code ?? "",
            dishes: dishes
                .filter((el) => el.name.trim() !== "")
                .map((el) => {
                    const { price, currency, ...restElements } = el
                    return {
                        ...restElements,
                        ...(price && currency
                            ? {
                                  price: toMonetaryAmount(toDineroLossy(price!, toCurrency(currency))),
                              }
                            : {}),
                    }
                }),
            ...(recurrence
                ? {
                      recurrence: {
                          ...recurrence,
                          ...(recurrence?.fromDate ? { fromDate: recurrence?.fromDate.toISODate() } : {}),
                          ...(recurrence?.toDate ? { toDate: recurrence?.toDate.toISODate() } : {}),
                      },
                  }
                : {}),
        }
        onSubmit(modifyDayMenu)
    }

    return (
        <>
            <Formik<CreateUpdateDayMenuFormValues>
                enableReinitialize
                initialValues={initialValues ? initialValues : defaultInitialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                <Form>
                    <CreateUpdateDayMenuFormContent isEditMode={!!initialValues} />
                </Form>
            </Formik>
        </>
    )
}
